import { convertHTMLStringToDOM } from '@/utils/convertHTMLStringToDOM'
import useTranslation from 'next-translate/useTranslation'
import { FC, useCallback, useState } from 'react'
import { BorderedButton } from '@/components/atoms/button'
import { Box } from '@/components/atoms/Grid'
import { Small, Small2Base } from '@/components/atoms/typography'
import styled from 'styled-components'

type ProductListingDescriptionProps = {
  description: string
  enableReadMore?: boolean
}

const getShortDescription = (originalText: string): string => {
  if (originalText.trim().length > 0) {
    let splitArray = originalText.split('</div>')
    let shorterText
    if (splitArray.length <= 2) {
      const re = /<br\s?\/?>/
      splitArray = originalText.split(re)
    }
    if (splitArray[0].trim().length === 0) {
      shorterText = splitArray[1]
    } else {
      shorterText = splitArray[0]
    }
    return shorterText
  }
  return originalText
}

const stripHtml = (text: string) => text.replace(/<\/?[^>]+(>|$)/g, '')

const ProductListingDescription: FC<ProductListingDescriptionProps> = ({
  description,
  enableReadMore = false
}) => {
  if ((description?.length ?? 0) === 0) {
    return null
  }

  const { t } = useTranslation('common')
  const [expanded, setExpanded] = useState<boolean>(false)

  const getDescription = useCallback(
    () =>
      enableReadMore && !expanded
        ? getShortDescription(description)
        : description,
    [enableReadMore, description, expanded]
  )

  const isExtendable = useCallback(
    () =>
      !enableReadMore
        ? false
        : stripHtml(description) !== stripHtml(getDescription()),
    [enableReadMore, description]
  )

  return (
    <Box
      textAlign="center"
      display={{ mobile: 'none', tablet: 'none', desktop: 'block' }}
    >
      <StyledSmall2>{convertHTMLStringToDOM(getDescription())}</StyledSmall2>
      {isExtendable() && (
        <>
          {expanded ? (
            <ExpandButton
              mb={14}
              title={t('Manufacturer.readLess')}
              onClick={() => setExpanded(false)}
            />
          ) : (
            <ExpandButton
              mb={14}
              title={t('Manufacturer.readMore')}
              onClick={() => setExpanded(true)}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default ProductListingDescription

const ExpandButton = styled(BorderedButton)`
  align-items: center;
  text-transform: uppercase;
  padding: 0 25px;
  height: 38px;

  & ${Small} {
    font-size: 12px;
    font-weight: 500;
  }
`

const StyledSmall2 = styled.div`
  ${Small2Base}
  margin: 14px 0;
  text-align: left;
  & a {
    color: ${({ theme }) => theme.colors.onBackground};
    text-decoration: none;
  }
  & a:hover {
    text-decoration: underline;
  }
`
